import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { usePageTransition } from '../../utils/pageTransitions';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';
import Button from '../../components/ui/Button';
import { contentService } from '../../services/contentService';

const ArticleDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { variants, transition } = usePageTransition();
  const [article, setArticle] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchArticle();
  }, [id]);

  const fetchArticle = async () => {
    try {
      setLoading(true);
      setError(null);

      const allContent = await contentService?.getAll();

      const found = allContent?.find((item) => item?.id === id);

      if (!found) {
        setError('Article introuvable');
        return;
      }

      setArticle(found);

      // Get related articles (same type, excluding current)
      const related = allContent
        ?.filter(
          (item) =>
            item?.id !== id &&
            item?.status === 'published' &&
            item?.type !== 'page'
        )
        ?.slice(0, 3) || [];

      setRelatedArticles(related);
    } catch (err) {
      console.error('Error fetching article:', err);
      setError('Impossible de charger l\'article');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date?.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: article?.title,
        text: article?.excerpt,
        url: window.location?.href,
      });
    } else {
      navigator.clipboard?.writeText(window.location?.href);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-white dark:bg-slate-900">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-2 border-blue-500 border-t-transparent mb-4" />
            <p className="text-slate-500 dark:text-slate-400">Chargement de l'article...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="min-h-screen flex flex-col bg-white dark:bg-slate-900">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 bg-red-500/10 border border-red-500/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Icon name="AlertCircle" size={36} className="text-red-400" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">Article introuvable</h2>
            <p className="text-slate-600 dark:text-slate-400 mb-6">{error || 'Cet article n\'existe pas ou a été supprimé.'}</p>
            <Button
              onClick={() => navigate('/news-articles-hub')}
              iconName="ArrowLeft"
              iconPosition="left"
              className="bg-blue-600 hover:bg-blue-500 text-white border-none rounded-xl px-6 py-2.5"
            >
              Retour aux articles
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{article?.title} - ORAZEK Investment Blog</title>
        <meta name="description" content={article?.excerpt || `Lisez l'article : ${article?.title}`} />
      </Helmet>
      <Header />
      <motion.main
        className="flex-1 bg-white dark:bg-slate-900"
        initial="initial"
        animate="animate"
        exit="exit"
        variants={variants}
        transition={transition}
      >
        {/* Hero Image Section */}
        <section className="relative h-[50vh] min-h-[400px] max-h-[600px] overflow-hidden">
          <Image
            src={article?.featuredImage || 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=1600'}
            alt={article?.featuredImageAlt || article?.title}
            className="w-full h-full object-cover"
          />
          {/* Dark gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-white via-white/60 to-white/20 dark:from-slate-900 dark:via-slate-900/60 dark:to-slate-900/20" />

          {/* Back button */}
          <div className="absolute top-24 left-0 w-full">
            <div className="w-full max-w-[1920px] mx-auto px-6 sm:px-12 lg:px-16">
              <motion.button
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
                onClick={() => navigate('/news-articles-hub')}
                className="flex items-center gap-2 bg-white/60 dark:bg-slate-900/60 backdrop-blur-sm border border-slate-200 dark:border-white/10 rounded-xl px-4 py-2.5 text-sm text-slate-900 dark:text-white hover:bg-white/80 dark:hover:bg-slate-900/80 transition-all"
              >
                <Icon name="ArrowLeft" size={16} />
                Retour aux articles
              </motion.button>
            </div>
          </div>

          {/* Article meta on hero */}
          <div className="absolute bottom-0 left-0 w-full">
            <div className="w-full max-w-[1920px] mx-auto px-6 sm:px-12 lg:px-16 pb-10">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                {/* Category + Date */}
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="bg-blue-600 text-white text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full">
                    {article?.type === 'blog' ? 'Blog' : article?.type === 'announcement' ? 'Annonce' : article?.type}
                  </span>
                  <div className="flex items-center gap-1.5 text-slate-600 dark:text-slate-300 text-sm">
                    <Icon name="Calendar" size={14} className="text-blue-400" />
                    <span>{formatDate(article?.publishedAt || article?.createdAt)}</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-slate-600 dark:text-slate-300 text-sm">
                    <Icon name="User" size={14} className="text-blue-400" />
                    <span>{article?.authorName || 'Admin ORAZEK'}</span>
                  </div>
                </div>

                {/* Title */}
                <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-slate-900 dark:text-white max-w-4xl leading-tight" style={{ fontFamily: 'var(--font-headline)' }}>
                  {article?.title}
                </h1>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Article Content Section */}
        <section className="relative py-12 lg:py-20 bg-white dark:bg-slate-900">
          {/* Noise texture */}
          <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='1'/%3E%3C/svg%3E")`, backgroundRepeat: 'repeat' }} />

          <div className="relative w-full max-w-[1920px] mx-auto px-6 sm:px-12 lg:px-16">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
              {/* Main Article Content */}
              <motion.article
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="lg:col-span-8"
              >
                {/* Excerpt / Lead paragraph */}
                {article?.excerpt && (
                  <div className="bg-blue-500/5 border-l-4 border-blue-500 p-5 rounded-r-2xl mb-10">
                    <p className="text-lg md:text-xl text-slate-600 dark:text-slate-300 italic leading-relaxed">
                      {article?.excerpt}
                    </p>
                  </div>
                )}

                {/* Article Body */}
                <div className="prose prose-lg dark:prose-invert max-w-none
                  prose-headings:text-slate-900 dark:prose-headings:text-white prose-headings:font-bold
                  prose-h2:text-2xl prose-h2:md:text-3xl prose-h2:mt-10 prose-h2:mb-4
                  prose-h3:text-xl prose-h3:md:text-2xl prose-h3:mt-8 prose-h3:mb-3
                  prose-p:text-slate-600 dark:prose-p:text-slate-300 prose-p:leading-relaxed prose-p:mb-6
                  prose-a:text-blue-400 prose-a:no-underline hover:prose-a:underline
                  prose-strong:text-slate-900 dark:prose-strong:text-white
                  prose-ul:text-slate-600 dark:prose-ul:text-slate-300 prose-ol:text-slate-600 dark:prose-ol:text-slate-300
                  prose-li:mb-2
                  prose-blockquote:border-blue-500 prose-blockquote:bg-blue-500/5 prose-blockquote:rounded-r-xl prose-blockquote:py-1 prose-blockquote:text-slate-600 dark:prose-blockquote:text-slate-300
                  prose-img:rounded-2xl prose-img:shadow-2xl
                  prose-code:text-blue-600 dark:prose-code:text-blue-300 prose-code:bg-slate-100 dark:prose-code:bg-slate-800/60 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded
                  prose-pre:bg-slate-100 dark:prose-pre:bg-slate-800/60 prose-pre:border prose-pre:border-slate-200 dark:prose-pre:border-white/10 prose-pre:rounded-xl
                ">
                  <div dangerouslySetInnerHTML={{ __html: article?.body || '' }} />
                </div>

                {/* Share & Actions */}
                <div className="mt-12 pt-8 border-t border-slate-200 dark:border-white/10">
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-semibold text-slate-500 dark:text-slate-400">Partager cet article :</span>
                      <button
                        onClick={handleShare}
                        className="flex items-center gap-2 bg-slate-100 dark:bg-slate-800/60 border border-slate-200 dark:border-white/10 text-blue-400 hover:bg-slate-200 dark:hover:bg-slate-800 hover:border-blue-500/30 rounded-xl px-4 py-2 text-sm font-medium transition-all"
                      >
                        <Icon name="Share2" size={16} />
                        Partager
                      </button>
                    </div>
                    <button
                      onClick={() => navigate('/news-articles-hub')}
                      className="flex items-center gap-2 text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white text-sm transition-colors"
                    >
                      <Icon name="ArrowLeft" size={16} />
                      Retour aux articles
                    </button>
                  </div>
                </div>
              </motion.article>

              {/* Sidebar */}
              <motion.aside
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="lg:col-span-4"
              >
                {/* Author Card */}
                <div className="bg-white/80 dark:bg-slate-800/40 backdrop-blur-xl border border-slate-200 dark:border-white/10 rounded-3xl p-6 mb-6 shadow-2xl">
                  <h3 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-4">Auteur</h3>
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-gradient-to-br from-blue-600 to-blue-800 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-900/30">
                      <Icon name="User" size={24} className="text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900 dark:text-white">{article?.authorName || 'Admin ORAZEK'}</p>
                      <p className="text-sm text-slate-600 dark:text-slate-400">Équipe ORAZEK Investment</p>
                    </div>
                  </div>
                </div>

                {/* Article Info Card */}
                <div className="bg-white/80 dark:bg-slate-800/40 backdrop-blur-xl border border-slate-200 dark:border-white/10 rounded-3xl p-6 mb-6 shadow-2xl">
                  <h3 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-4">Informations</h3>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 text-sm">
                      <Icon name="Calendar" size={16} className="text-blue-400 flex-shrink-0" />
                      <span className="text-slate-500 dark:text-slate-400">Publié le</span>
                      <span className="text-slate-900 dark:text-white ml-auto">{formatDate(article?.publishedAt || article?.createdAt)}</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm">
                      <Icon name="Tag" size={16} className="text-blue-400 flex-shrink-0" />
                      <span className="text-slate-500 dark:text-slate-400">Catégorie</span>
                      <span className="text-slate-900 dark:text-white ml-auto capitalize">{article?.type === 'blog' ? 'Blog' : article?.type === 'announcement' ? 'Annonce' : article?.type}</span>
                    </div>
                  </div>
                </div>

                {/* Related Articles */}
                {relatedArticles?.length > 0 && (
                  <div className="bg-white/80 dark:bg-slate-800/40 backdrop-blur-xl border border-slate-200 dark:border-white/10 rounded-3xl p-6 shadow-2xl">
                    <h3 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-4">Autres articles</h3>
                    <div className="space-y-4">
                      {relatedArticles?.map((related) => (
                        <button
                          key={related?.id}
                          onClick={() => navigate(`/news-articles-hub/${related?.id}`)}
                          className="w-full text-left group"
                        >
                          <div className="flex gap-3">
                            <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0">
                              <Image
                                src={related?.featuredImage || 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=200'}
                                alt={related?.title}
                                className="w-full h-full object-cover"
                              />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="text-sm font-semibold text-slate-900 dark:text-white group-hover:text-blue-400 transition-colors line-clamp-2 mb-1">
                                {related?.title}
                              </h4>
                              <p className="text-xs text-slate-500 dark:text-slate-400">
                                {formatDate(related?.publishedAt || related?.createdAt)}
                              </p>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </motion.aside>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative py-20 lg:py-28 bg-white dark:bg-slate-900 overflow-hidden">
          <div className="w-full max-w-[1920px] mx-auto px-6 sm:px-12 lg:px-16">
            <div className="relative bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 rounded-[2.5rem] p-10 md:p-16 overflow-hidden">
              <motion.div
                className="absolute top-0 right-0 w-[400px] h-[400px] rounded-full bg-white/5 blur-3xl"
                animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
                transition={{ duration: 14, repeat: Infinity, ease: 'easeInOut' }}
              />
              <motion.div
                className="absolute bottom-0 left-0 w-[300px] h-[300px] rounded-full bg-amber-500/10 blur-3xl"
                animate={{ scale: [1.2, 1, 1.2], opacity: [0.2, 0.4, 0.2] }}
                transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut' }}
              />

              <div className="relative text-center">
                <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Icon name="Newspaper" size={32} className="text-white" />
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'var(--font-headline)' }}>
                  Découvrez plus d'articles
                </h2>
                <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
                  Explorez toutes nos actualités, analyses et insights sur le marché congolais
                </p>
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Button
                    onClick={() => navigate('/news-articles-hub')}
                    variant="default"
                    size="lg"
                    className="bg-white text-blue-700 hover:bg-white/90 shadow-2xl border-none rounded-xl px-8 py-3 font-semibold"
                    iconName="ArrowRight"
                    iconPosition="right"
                  >
                    Voir tous les articles
                  </Button>
                </motion.div>
              </div>
            </div>
          </div>
        </section>
      </motion.main>
      <Footer />
    </>
  );
};

export default ArticleDetailPage;
